import React, { useCallback, useMemo, useState } from 'react';

import { useDebouncedValue, useUpdateEffect } from '@private/hooks';
import { useQueryClient } from '@tanstack/react-query';

import { IFilterPayload } from 'api/portalUser/types';
import { getFiltersToSave } from 'components/Grids/DataGrid/Filters/Presets/helpers';
import {
  FILTER_OPERATIONS,
  IAppliedFilterValue,
  NAVIGATION_ACTION,
} from 'components/Grids/DataGrid/Filters/utils/types';
import { useGetPortalUserConfig, useSaveFiltersPreset } from 'queries/portalUser';
import { IAllPageFilters, IPageFiltersChangeProps, TSortProps } from 'utils/types';

import { useSaveInURL } from '../useSaveInURL';

import {
  composeInitialFilters,
  getPresets,
  handleSearchFilterState,
  RECENT_FILTER_NAME,
  removeHiddenColumnsFromSearch,
} from './helpers';
import {
  ISearchProps,
  IUrlFilterSettings,
  IUsePageNavigation,
  IUsePageNavigationResult,
} from './types';
import { useUrlSearchQuery } from './useUrlSearchQuery';

export const usePageNavigation = ({
  customSortChange,
  disableUrlFilters,
  fallbackValues,
  hiddenColumns,
  initialFilters,
  pageKey,
  saveInitialSearch,
}: IUsePageNavigation = {}): IUsePageNavigationResult => {
  const { onSave: setUrlFilters, savedData: urlFilters } =
    useSaveInURL<IUrlFilterSettings>('pageFilters');

  const urlSearchQuery = useUrlSearchQuery();

  const { data: portalUser } = useGetPortalUserConfig();
  const { mutate: savePreset } = useSaveFiltersPreset();

  const isUrlFilters =
    !!(urlFilters?.search && urlFilters.columns && !disableUrlFilters) ||
    !!urlSearchQuery;

  /*
    Preset activation updates portalUser and we need to update pageFilters.
    But when we choose REDIRECTED_PRESET in preset options, portalUser stays same.
    That's why we need to set the isRedirectedPreset status and the corresponding page filter updates.
  */
  const [isRedirectedPreset, setIsRedirectedPreset] = useState(isUrlFilters);

  const { activeFilterPresetId, presetFilters } = useMemo(() => {
    const { activeFilterPreset, activeFilterPresetId } = getPresets({
      pageKey,
      portalUser,
    });
    return { activeFilterPresetId, presetFilters: activeFilterPreset?.filters || {} };
  }, [portalUser, pageKey]);

  const [pageFilters, setPageFilters] = useState<IAllPageFilters>(
    composeInitialFilters({
      disableUrlFilters,
      hiddenColumns,
      initialFilters,
      presetFilters,
      recordsPerPage: initialFilters?.size || portalUser?.settings.recordsPerPage,
      urlFilters,
      urlSearchQuery,
    }),
  );

  const presetToSave = React.useRef<IFilterPayload>(undefined);
  const lastNavigationAction = React.useRef<NAVIGATION_ACTION>(undefined);
  const prevActivePresetIdRef = React.useRef(activeFilterPresetId);
  const [previousFilters, setPreviousFilters] = useState<IAllPageFilters>(pageFilters);
  const [debouncedPageFilters, setDebouncedPageFilters] =
    useDebouncedValue<IAllPageFilters>(500, pageFilters);

  const { page, sort } = pageFilters;

  const queryClient = useQueryClient();

  React.useEffect(() => {
    const cache = queryClient.getQueryCache();

    return cache.subscribe((event) => {
      if (event.type !== 'updated') return;

      const { query } = event;
      const meta = query.meta ?? {};

      if (
        meta.commitRecentPreset &&
        meta.pageKey &&
        presetToSave.current &&
        query.state.status === 'success'
      ) {
        savePreset(presetToSave.current);
        presetToSave.current = undefined;
      }
    });
  }, []);

  useUpdateEffect(() => {
    if (initialFilters) {
      setPageFilters((state) => {
        const newFilters = composeInitialFilters({
          disableUrlFilters,
          hiddenColumns,
          initialFilters,
          presetFilters,
          recordsPerPage: portalUser?.settings.recordsPerPage,
          urlFilters,
          urlSearchQuery: isRedirectedPreset ? urlSearchQuery : undefined,
        });

        lastNavigationAction.current = NAVIGATION_ACTION.ALL;
        setPreviousFilters(state);
        setDebouncedPageFilters(newFilters, true);
        return newFilters;
      });
    }
  }, [initialFilters]);

  useUpdateEffect(() => {
    if (!disableUrlFilters) {
      setUrlFilters({ page, sort });
    }
  }, [disableUrlFilters, page, sort]);

  useUpdateEffect(() => {
    const presetIdChanged = prevActivePresetIdRef.current !== activeFilterPresetId;
    prevActivePresetIdRef.current = activeFilterPresetId;

    if (isRedirectedPreset) {
      return;
    }

    // Skip when this fire was triggered solely by isRedirectedPreset flipping
    // (e.g., user modified filters on a shared link). Re-applying presetFilters
    // here would overwrite the just-modified pageFilters.search with a stale
    // Recent preset, causing a rollback to previously shared filters.
    if (!presetIdChanged) {
      return;
    }

    const initialSearch = initialFilters?.search || {};

    let search = saveInitialSearch
      ? { ...presetFilters, ...initialSearch }
      : presetFilters;

    if (fallbackValues) {
      const fallbackEntries = Object.entries<() => IAppliedFilterValue>(
        fallbackValues,
      ).map(([columnKey, filterCb]) => [columnKey, filterCb()]);
      const fallbacks = Object.fromEntries(fallbackEntries);
      search = { ...fallbacks, ...search };
    }

    setPageFilters((state) => {
      const newFilter = {
        ...state,
        search: removeHiddenColumnsFromSearch(
          search as IAllPageFilters['search'],
          hiddenColumns,
        ),
      };
      lastNavigationAction.current = NAVIGATION_ACTION.PRESET;
      setDebouncedPageFilters(newFilter, true);
      return newFilter;
    });
  }, [activeFilterPresetId, isRedirectedPreset]);

  useUpdateEffect(() => {
    if (isRedirectedPreset) {
      setPageFilters((state) => {
        const newFilter = {
          ...state,
          search: removeHiddenColumnsFromSearch(
            urlSearchQuery ? { ...urlSearchQuery } : { ...urlFilters?.search },
            hiddenColumns,
          ),
        };
        lastNavigationAction.current = NAVIGATION_ACTION.PRESET;
        setDebouncedPageFilters(newFilter, true);
        return newFilter;
      });
    }
  }, [isRedirectedPreset]);

  const rollBackBrokenFilters = React.useCallback(() => {
    setDebouncedPageFilters(previousFilters, true);
    setPageFilters(previousFilters);
  }, [previousFilters]);

  const onPerPageChange = useCallback((count: number) => {
    setPageFilters((state) => {
      setPreviousFilters(state);
      const newFilter = { ...state, page: 0, size: count };
      lastNavigationAction.current = NAVIGATION_ACTION.PER_PAGE;
      setDebouncedPageFilters(newFilter, true);
      return newFilter;
    });
  }, []);

  const onPageChange = useCallback((page: number) => {
    setPageFilters((state) => {
      const newFilter = { ...state, page };
      setPreviousFilters(state);
      lastNavigationAction.current = NAVIGATION_ACTION.PAGE;
      setDebouncedPageFilters(newFilter, true);
      return newFilter;
    });
  }, []);

  const onSearchChange = useCallback((searchProps: ISearchProps) => {
    setPageFilters(({ search, ...state }) => {
      const newFilter = {
        ...state,
        page: 0,
        search: handleSearchFilterState({ search, searchProps }),
      };
      lastNavigationAction.current = NAVIGATION_ACTION.FILTER;
      setPreviousFilters(state);
      setDebouncedPageFilters(newFilter);
      return newFilter;
    });
  }, []);

  const onSortChange = useCallback((config: TSortProps) => {
    if (config) {
      setPageFilters((state) => {
        const newFilter = {
          ...state,
          sort: {
            [config?.property]: {
              ignoreCase: true,
              operation: ',' as FILTER_OPERATIONS,
              value: config?.direction,
            },
          },
        };
        lastNavigationAction.current = NAVIGATION_ACTION.SORT;
        setPreviousFilters(state);
        setDebouncedPageFilters(newFilter, true);
        return newFilter;
      });
    } else {
      setPageFilters((state) => {
        const newFilter = { ...state, sort: {} };
        lastNavigationAction.current = NAVIGATION_ACTION.SORT;
        setPreviousFilters(state);
        setDebouncedPageFilters(newFilter, true);
        return newFilter;
      });
    }
  }, []);

  const onPageFiltersChange = useCallback(
    ({ filters, gridColumnsSettings }: IPageFiltersChangeProps) => {
      const initialSearch = initialFilters?.search || {};

      const newFilters = filters || pageFilters.search || {};

      setPageFilters((state) => {
        const newState = {
          ...state,
          page: 0,
          search: saveInitialSearch ? { ...newFilters, ...initialSearch } : newFilters,
        };

        if (state?.search?.localSearch) {
          newState.search.localSearch = state?.search?.localSearch;
        }

        lastNavigationAction.current = NAVIGATION_ACTION.FILTER;
        setPreviousFilters(state);
        setDebouncedPageFilters(newState, true);

        return newState;
      });

      if (pageKey) {
        presetToSave.current = {
          columns: gridColumnsSettings,
          filters: getFiltersToSave({ search: filters }),
          isRecent: true,
          name: RECENT_FILTER_NAME,
          pageKey,
        };

        setIsRedirectedPreset(false);
      }
    },
    [initialFilters?.search, pageFilters.search, pageKey, saveInitialSearch, savePreset],
  );

  return {
    isRedirectedPreset,
    lastNavigationAction: lastNavigationAction.current,
    localSearch: (pageFilters.search?.localSearch?.value as string) || '',
    onPageChange,
    onPageFiltersChange,
    onPerPageChange,
    onSearchChange,
    onSortChange: customSortChange || onSortChange,
    pageFilters: debouncedPageFilters || pageFilters,
    pageKey,
    rollBackBrokenFilters,
    setIsRedirectedPreset,
  };
};
